import { useState, useEffect } from 'react';
import { 
  Megaphone, 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  Tag, 
  ShoppingCart, 
  AlertTriangle,
  X,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

interface Product {
  id: string;
  name: string;
  price: number;
  image?: string;
  description?: string;
}

interface Campaign {
  id: string;
  name: string;
  description: string;
  campaignId: string;
  triggerPhrase: string;
  active: boolean;
  products: Product[];
}

const Campanhas = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewCampaign, setShowNewCampaign] = useState(false);
  const [showAddProduct, setShowAddProduct] = useState<string | null>(null);
  const [expandedCampaign, setExpandedCampaign] = useState<string | null>(null);
  const [editingCampaign, setEditingCampaign] = useState<string | null>(null);
  const [newCampaign, setNewCampaign] = useState<Partial<Campaign>>({
    name: '',
    description: '',
    campaignId: '',
    triggerPhrase: '',
    active: true,
    products: []
  });
  const [newProduct, setNewProduct] = useState<Partial<Product>>({
    name: '',
    price: 0,
    image: '',
    description: ''
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Load campaigns from API or localStorage
    const loadCampaigns = async () => {
      try {
        // For demo, we'll use localStorage
        const savedCampaigns = localStorage.getItem('campaigns');
        if (savedCampaigns) {
          setCampaigns(JSON.parse(savedCampaigns));
        } else {
          // Sample data
          const sampleCampaigns: Campaign[] = [
            {
              id: '1',
              name: 'Campanha de Verão',
              description: 'Produtos para o verão com descontos especiais',
              campaignId: 'VERAO2025',
              triggerPhrase: 'verão',
              active: true,
              products: [
                {
                  id: '101',
                  name: 'Protetor Solar FPS 50',
                  price: 29.99,
                  image: 'https://images.pexels.com/photos/4239091/pexels-photo-4239091.jpeg',
                  description: 'Proteção completa contra raios UVA e UVB'
                },
                {
                  id: '102',
                  name: 'Óculos de Sol Premium',
                  price: 89.99,
                  image: 'https://images.pexels.com/photos/701877/pexels-photo-701877.jpeg',
                  description: 'Proteção UV com estilo'
                }
              ]
            },
            {
              id: '2',
              name: 'Black Friday',
              description: 'Ofertas imperdíveis para a Black Friday',
              campaignId: 'BF2025',
              triggerPhrase: 'black friday',
              active: false,
              products: [
                {
                  id: '201',
                  name: 'Smartphone XYZ',
                  price: 999.99,
                  image: 'https://images.pexels.com/photos/47261/pexels-photo-47261.jpeg',
                  description: 'O smartphone mais avançado do mercado'
                }
              ]
            }
          ];
          setCampaigns(sampleCampaigns);
          localStorage.setItem('campaigns', JSON.stringify(sampleCampaigns));
        }
      } catch (error) {
        console.error('Error loading campaigns:', error);
      } finally {
        setLoading(false);
      }
    };

    loadCampaigns();
  }, []);

  const saveCampaigns = (updatedCampaigns: Campaign[]) => {
    localStorage.setItem('campaigns', JSON.stringify(updatedCampaigns));
    setCampaigns(updatedCampaigns);
  };

  const handleCreateCampaign = () => {
    if (!newCampaign.name || !newCampaign.campaignId) {
      alert('Nome da campanha e ID da campanha são obrigatórios');
      return;
    }

    const campaign: Campaign = {
      id: Date.now().toString(),
      name: newCampaign.name || '',
      description: newCampaign.description || '',
      campaignId: newCampaign.campaignId || '',
      triggerPhrase: newCampaign.triggerPhrase || '',
      active: newCampaign.active || false,
      products: []
    };

    const updatedCampaigns = [...campaigns, campaign];
    saveCampaigns(updatedCampaigns);
    
    setNewCampaign({
      name: '',
      description: '',
      campaignId: '',
      triggerPhrase: '',
      active: true,
      products: []
    });
    setShowNewCampaign(false);
  };

  const handleUpdateCampaign = (id: string, updates: Partial<Campaign>) => {
    const updatedCampaigns = campaigns.map(campaign => 
      campaign.id === id ? { ...campaign, ...updates } : campaign
    );
    saveCampaigns(updatedCampaigns);
    setEditingCampaign(null);
  };

  const handleDeleteCampaign = (id: string) => {
    if (window.confirm('Tem certeza que deseja excluir esta campanha?')) {
      const updatedCampaigns = campaigns.filter(campaign => campaign.id !== id);
      saveCampaigns(updatedCampaigns);
    }
  };

  const handleAddProduct = (campaignId: string) => {
    if (!newProduct.name || !newProduct.price) {
      alert('Nome e preço do produto são obrigatórios');
      return;
    }

    const product: Product = {
      id: Date.now().toString(),
      name: newProduct.name || '',
      price: newProduct.price || 0,
      image: newProduct.image,
      description: newProduct.description
    };

    const updatedCampaigns = campaigns.map(campaign => {
      if (campaign.id === campaignId) {
        return {
          ...campaign,
          products: [...campaign.products, product]
        };
      }
      return campaign;
    });

    saveCampaigns(updatedCampaigns);
    
    setNewProduct({
      name: '',
      price: 0,
      image: '',
      description: ''
    });
    setShowAddProduct(null);
  };

  const handleDeleteProduct = (campaignId: string, productId: string) => {
    if (window.confirm('Tem certeza que deseja excluir este produto?')) {
      const updatedCampaigns = campaigns.map(campaign => {
        if (campaign.id === campaignId) {
          return {
            ...campaign,
            products: campaign.products.filter(product => product.id !== productId)
          };
        }
        return campaign;
      });
      saveCampaigns(updatedCampaigns);
    }
  };

  const toggleCampaignExpansion = (campaignId: string) => {
    setExpandedCampaign(expandedCampaign === campaignId ? null : campaignId);
  };

  const filteredCampaigns = campaigns.filter(campaign => 
    campaign.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    campaign.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    campaign.campaignId.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Campanhas</h1>
          <p className="text-gray-600 mt-1">Gerencie campanhas e produtos para marketing</p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar campanhas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <button
            onClick={() => setShowNewCampaign(true)}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nova Campanha
          </button>
        </div>
      </div>

      {/* Demo Mode Notice */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
        <div className="flex items-start">
          <AlertTriangle className="h-6 w-6 text-yellow-600 mr-3 mt-0.5" />
          <div>
            <h3 className="text-lg font-semibold text-yellow-800 mb-2">
              Modo de Demonstração
            </h3>
            <p className="text-yellow-700">
              Esta é uma demonstração da funcionalidade de Campanhas. Em uma implementação completa, você poderia:
            </p>
            <ul className="mt-2 ml-6 list-disc text-yellow-700">
              <li>Criar campanhas de marketing com produtos específicos</li>
              <li>Associar códigos de identificação para rastreamento de leads</li>
              <li>Configurar a IA para focar nos produtos da campanha</li>
              <li>Analisar métricas de conversão por campanha</li>
            </ul>
          </div>
        </div>
      </div>

      {/* New Campaign Form */}
      {showNewCampaign && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Nova Campanha</h3>
            <button
              onClick={() => setShowNewCampaign(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nome da Campanha
              </label>
              <input
                type="text"
                value={newCampaign.name}
                onChange={(e) => setNewCampaign({...newCampaign, name: e.target.value})}
                placeholder="Ex: Campanha de Verão"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ID da Campanha
              </label>
              <input
                type="text"
                value={newCampaign.campaignId}
                onChange={(e) => setNewCampaign({...newCampaign, campaignId: e.target.value})}
                placeholder="Ex: VERAO2025"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Descrição
              </label>
              <textarea
                value={newCampaign.description}
                onChange={(e) => setNewCampaign({...newCampaign, description: e.target.value})}
                rows={2}
                placeholder="Descreva o objetivo da campanha"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Frase de Gatilho
              </label>
              <input
                type="text"
                value={newCampaign.triggerPhrase}
                onChange={(e) => setNewCampaign({...newCampaign, triggerPhrase: e.target.value})}
                placeholder="Ex: verão, proteção solar"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                Palavras-chave que ativam esta campanha quando mencionadas pelo usuário
              </p>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="active"
                checked={newCampaign.active}
                onChange={(e) => setNewCampaign({...newCampaign, active: e.target.checked})}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="active" className="ml-2 block text-sm text-gray-900">
                Campanha Ativa
              </label>
            </div>
          </div>
          <div className="flex justify-end mt-6">
            <button
              onClick={() => setShowNewCampaign(false)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 mr-2 hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleCreateCampaign}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Criar Campanha
            </button>
          </div>
        </div>
      )}

      {/* Campaigns List */}
      <div className="space-y-4">
        {filteredCampaigns.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <Megaphone className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhuma campanha encontrada</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm ? 'Tente ajustar sua busca' : 'Comece criando sua primeira campanha'}
            </p>
            <div className="mt-6">
              <button
                onClick={() => setShowNewCampaign(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Plus className="h-4 w-4 mr-2" />
                Nova Campanha
              </button>
            </div>
          </div>
        ) : (
          filteredCampaigns.map((campaign) => (
            <div key={campaign.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div className="flex items-center">
                  <div className={`p-3 rounded-full ${campaign.active ? 'bg-green-100' : 'bg-gray-100'}`}>
                    <Megaphone className={`h-6 w-6 ${campaign.active ? 'text-green-600' : 'text-gray-500'}`} />
                  </div>
                  <div className="ml-4">
                    <div className="flex items-center">
                      <h3 className="text-lg font-semibold text-gray-900">{campaign.name}</h3>
                      <span className={`ml-2 px-2 py-1 text-xs font-medium rounded-full ${
                        campaign.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {campaign.active ? 'Ativa' : 'Inativa'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{campaign.description}</p>
                    <div className="flex items-center mt-1 space-x-4">
                      <div className="flex items-center text-sm text-gray-500">
                        <Tag className="h-4 w-4 mr-1" />
                        <span>ID: {campaign.campaignId}</span>
                      </div>
                      <div className="flex items-center text-sm text-gray-500">
                        <ShoppingCart className="h-4 w-4 mr-1" />
                        <span>{campaign.products.length} produtos</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => toggleCampaignExpansion(campaign.id)}
                    className="p-2 text-gray-400 hover:text-blue-600"
                  >
                    {expandedCampaign === campaign.id ? (
                      <ChevronUp className="h-5 w-5" />
                    ) : (
                      <ChevronDown className="h-5 w-5" />
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setEditingCampaign(campaign.id);
                      setNewCampaign({
                        name: campaign.name,
                        description: campaign.description,
                        campaignId: campaign.campaignId,
                        triggerPhrase: campaign.triggerPhrase,
                        active: campaign.active
                      });
                    }}
                    className="p-2 text-gray-400 hover:text-blue-600"
                  >
                    <Edit className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleDeleteCampaign(campaign.id)}
                    className="p-2 text-gray-400 hover:text-red-600"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Edit Campaign Form */}
              {editingCampaign === campaign.id && (
                <div className="p-6 border-b border-gray-200 bg-gray-50">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nome da Campanha
                      </label>
                      <input
                        type="text"
                        value={newCampaign.name}
                        onChange={(e) => setNewCampaign({...newCampaign, name: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        ID da Campanha
                      </label>
                      <input
                        type="text"
                        value={newCampaign.campaignId}
                        onChange={(e) => setNewCampaign({...newCampaign, campaignId: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Descrição
                      </label>
                      <textarea
                        value={newCampaign.description}
                        onChange={(e) => setNewCampaign({...newCampaign, description: e.target.value})}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Frase de Gatilho
                      </label>
                      <input
                        type="text"
                        value={newCampaign.triggerPhrase}
                        onChange={(e) => setNewCampaign({...newCampaign, triggerPhrase: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id={`active-${campaign.id}`}
                        checked={newCampaign.active}
                        onChange={(e) => setNewCampaign({...newCampaign, active: e.target.checked})}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor={`active-${campaign.id}`} className="ml-2 block text-sm text-gray-900">
                        Campanha Ativa
                      </label>
                    </div>
                  </div>
                  <div className="flex justify-end mt-4">
                    <button
                      onClick={() => setEditingCampaign(null)}
                      className="px-3 py-1 border border-gray-300 rounded-lg text-gray-700 mr-2 hover:bg-gray-50"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={() => handleUpdateCampaign(campaign.id, newCampaign)}
                      className="px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      Salvar Alterações
                    </button>
                  </div>
                </div>
              )}

              {/* Campaign Products */}
              {expandedCampaign === campaign.id && (
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-sm font-medium text-gray-900">Produtos da Campanha</h4>
                    <button
                      onClick={() => setShowAddProduct(campaign.id)}
                      className="flex items-center px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Adicionar Produto
                    </button>
                  </div>

                  {/* Add Product Form */}
                  {showAddProduct === campaign.id && (
                    <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Nome do Produto
                          </label>
                          <input
                            type="text"
                            value={newProduct.name}
                            onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Preço ($)
                          </label>
                          <input
                            type="number"
                            value={newProduct.price}
                            onChange={(e) => setNewProduct({...newProduct, price: parseFloat(e.target.value)})}
                            step="0.01"
                            min="0"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            URL da Imagem
                          </label>
                          <input
                            type="text"
                            value={newProduct.image}
                            onChange={(e) => setNewProduct({...newProduct, image: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Descrição
                          </label>
                          <input
                            type="text"
                            value={newProduct.description}
                            onChange={(e) => setNewProduct({...newProduct, description: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                      </div>
                      <div className="flex justify-end mt-4">
                        <button
                          onClick={() => setShowAddProduct(null)}
                          className="px-3 py-1 border border-gray-300 rounded-lg text-gray-700 mr-2 hover:bg-gray-50"
                        >
                          Cancelar
                        </button>
                        <button
                          onClick={() => handleAddProduct(campaign.id)}
                          className="px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                          Adicionar Produto
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Products List */}
                  {campaign.products.length === 0 ? (
                    <div className="text-center py-8 bg-gray-50 rounded-lg">
                      <ShoppingCart className="mx-auto h-10 w-10 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum produto</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Adicione produtos a esta campanha para começar
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {campaign.products.map((product) => (
                        <div key={product.id} className="border border-gray-200 rounded-lg overflow-hidden flex">
                          {product.image && (
                            <div className="w-24 h-24 flex-shrink-0">
                              <img 
                                src={product.image} 
                                alt={product.name} 
                                className="w-full h-full object-cover"
                              />
                            </div>
                          )}
                          <div className="p-4 flex-1">
                            <div className="flex justify-between">
                              <h5 className="text-sm font-medium text-gray-900">{product.name}</h5>
                              <button
                                onClick={() => handleDeleteProduct(campaign.id, product.id)}
                                className="text-gray-400 hover:text-red-600"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                            <p className="text-sm text-gray-600 mt-1">{product.description}</p>
                            <p className="text-sm font-bold text-green-600 mt-1">${product.price.toFixed(2)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Campanhas;